import { Box, Flex, Text } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/pro-light-svg-icons';
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import {
  Button,
  ButtonText,
  FormFeedback,
  Input,
  Modal,
  useToast
} from '@morressier/alembic';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FieldLabel } from '../Field';
import { useUpdatedUserInfoMutation } from 'src/api/signUpMutation';
import { useKeycloak } from '../AuthHOC';
import { validateEmail } from 'src/pages/Sign-Up/SignUpForm';
import { IUserType } from '../UserRegistrationSteps/StepThree/signupHook';
import { IUserProfileDetailsType } from './ProfileDetails';
import SessionUpdateModal from './SessionUpdateModal';

type UpdateEmailForm = {
  email: string;
};

const UpdateEmailModal = ({
  user,
  profileDetails
}: {
  user: IUserType;
  profileDetails: IUserProfileDetailsType;
}) => {
  const toast = useToast();
  const { logout } = useKeycloak();
  const updatedProfileInfoMutation = useUpdatedUserInfoMutation();

  const [isOpen, setIsOpen] = useState(false);
  const [isOpenConfirmationModal, setIsOpenConfirmationModal] = useState(false);

  const methods = useForm<UpdateEmailForm>({
    defaultValues: { email: '' },
    mode: 'onChange'
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = methods;

  const onClose = () => {
    reset({ email: '' });
    setIsOpen(false);
  };

  const onCloseConfirmationModal = () => {
    setIsOpenConfirmationModal(false);
    setIsOpen(false);
  };

  const handleUpdateEmailSubmit = async (values: UpdateEmailForm) => {
    const payload = {
      section: 'PERSONAL_INFO',
      firstName: user.firstName,
      lastName: user.lastName,
      middleName: user.middleName ?? '',
      prefix: user.prefix ?? '',
      degree: user.degree ?? '',
      jobTitle: user.jobTitle ?? '',
      orcid: user.orcid ?? '',
      email: values?.email?.trim(),
      secondaryEmail: user.secondaryEmail ?? '',
      ccEmail: user.ccEmail ?? '',
      additionalEmails: user.additionalEmails ?? [],
      profileDetails: {
        phoneNumber: profileDetails?.phoneNumber ?? '',
        countryCallingCode: profileDetails?.countryCallingCode ?? '',
        countryCode: profileDetails?.countryCode ?? '',
        linkedinProfileUrl: profileDetails?.linkedinProfileUrl ?? ''
      }
    };

    const response = await updatedProfileInfoMutation.mutateAsync({
      payload
    });

    if (!response.isSuccess) {
      const message = response?.message?.details?.length
        ? response.message.details[0]?.message
        : response?.message?.message || 'Internal Server Error';

      toast({
        variant: 'error',
        title: 'Error',
        description: message,
        isClosable: true
      });
      return;
    }

    toast({
      variant: 'success',
      title: 'Success',
      description: response?.message ?? 'Email updated successfully.',
      isClosable: true
    });

    setIsOpenConfirmationModal(true);
  };

  return (
    <>
      <SessionUpdateModal
        isOpen={isOpenConfirmationModal}
        onClose={() => {}}
        message={
          <>
            Your primary email has been updated successfully. <br />
            The change will take effect the next time you log in. <br />
            Would you like to re-login now to apply it immediately?
          </>
        }
        actionButton={
          <Flex justifyContent={'flex-end'} gap={3} mt={3}>
            <Button variant="secondary" onClick={onCloseConfirmationModal}>
              Exit
            </Button>
            <Button onClick={() => logout()}>Re-login</Button>
          </Flex>
        }
      />

      <ButtonText
        fontSize={'small'}
        onClick={() => {
          reset({ email: '' });
          setIsOpen(true);
        }}
        data-cy="update-email"
      >
        <FontAwesomeIcon icon={faEdit} />
        Edit
      </ButtonText>

      <Modal
        isOpen={isOpen}
        onClose={() => {}}
        closeButtonProps={{ visibility: 'hidden' }}
        width="700px"
      >
        <FormProvider {...methods}>
          <Flex direction={'column'} px={8} pt={8}>
            <Flex direction={'column'} gap={3}>
              <Box>
                <FontAwesomeIcon icon={faEnvelope} size="2xl" />
              </Box>
              <Text textStyle="headingGroup">Update Email</Text>
              <Text textStyle="textBody">Current email: {user.email}</Text>

              <form
                autoComplete="off"
                onSubmit={handleSubmit(handleUpdateEmailSubmit)}
              >
                <Flex direction={'column'} gap={5}>
                  <Flex direction={'column'} gap={3} flex={1}>
                    <FieldLabel
                      label="New Primary Email"
                      subText=""
                      isRequired
                    />
                    <Input
                      placeholder="Enter your new primary email address"
                      {...register('email', {
                        required: 'Primary email address is required',
                        maxLength: {
                          value: 128,
                          message:
                            'Primary email address must not exceed 128 characters'
                        },
                        pattern: {
                          value: validateEmail,
                          message: 'Please enter a valid primary email address'
                        },
                        validate: value =>
                          value.trim().toLowerCase() ===
                          user.email?.trim().toLowerCase()
                            ? 'New email address must be different from your current email address.'
                            : true
                      })}
                      isInvalid={!!errors.email}
                      aria-label={'primaryEmail'}
                    />
                    {errors.email && (
                      <FormFeedback variant="error">
                        {errors.email?.message}
                      </FormFeedback>
                    )}
                  </Flex>

                  <Flex justifyContent={'flex-end'} gap={3} mb={5}>
                    <Button variant="secondary" onClick={onClose}>
                      Exit Without Saving
                    </Button>
                    <Button
                      type="submit"
                      disabled={updatedProfileInfoMutation?.isLoading}
                    >
                      Save Changes
                    </Button>
                  </Flex>
                </Flex>
              </form>
            </Flex>
          </Flex>
        </FormProvider>
      </Modal>
    </>
  );
};

export default UpdateEmailModal;
